import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fromJS } from "immutable";
import { useDispatch, useSelector } from "react-redux";
import { CircleCheckBig, CreditCard, Sparkles, TriangleAlert } from "lucide-react";
import { PaymentElement, useCheckout } from "@stripe/react-stripe-js/checkout";
import LayoutAction from "../../actions/LayoutAction";
import BillingAction from "../../actions/BillingAction";
import { getBaseUrl } from "../../api/api";
import { SmallLoading } from "../../main/Loading";
import { formatAmount } from "../../api/DataTypeUtils";
import { getBillingAddress } from "../../api/BillingApi";

const CheckoutForm = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { projectId }: any = useParams();
  const checkoutState = useCheckout();

  const checkoutSession =
    useSelector((state: any) => state.billingReducer.get("checkoutSession")) || fromJS({});

  const selectedPlan = checkoutSession.get("selectedPlan");

  const [loading, setLoading] = useState(false);

  const billingAddressQuery = useQuery({
    queryKey: ["billing-address"],
    queryFn: () => getBillingAddress(),
  });

  const billingAddress = fromJS(billingAddressQuery.data || {});

  const handleClickPay = () => {
    if (checkoutState.type == "success") {
      const { confirm } = checkoutState.checkout;
      setLoading(true);
      const returnUrl = `${getBaseUrl()}/workspaces/${projectId}/settings/team?item=billing&key=Team%20Settings`;
      confirm({
        redirect: "if_required",
        returnUrl,
      }).then((result) => {
        if (result.type === "success") {
        } else if (result.type === "error") {
          dispatch(
            LayoutAction.setNotification({
              title: t("Payment"),
              type: "error",
              message: result.error.message || t("Failed to pay"),
            })
          );
        }
        setLoading(false);
      });
    }
  };

  const handleBack = () => {
    dispatch(BillingAction.setCheckoutSession(null));
  };

  const handleClose = () => {
    dispatch(BillingAction.getBillingUserLimits());
    dispatch(BillingAction.getBillingSubscriptionStatus());
    dispatch(BillingAction.hideSelectPlanDialog());
  };

  const getLimitUserText = (plan, currency) => {
    let price = 0;
    switch (plan.key) {
      case "FREE_1_USER":
        price = 0;
        break;
      case "PLUS_5_USERS":
        price = 50;
        break;
      case "PLUS_25_USERS":
        price = 100;
        break;
      case "ENTERPRISE_UNLIMITED":
        price = 300;
        break;
      default:
        price = 0;
        break;
    }
    const planMonney = formatAmount(i18n.language, price, currency);
    const automationLimit = Number(plan?.entitlements?.maxAutomationExecutionsPerMonth);
    const automationText =
      !Number.isFinite(automationLimit) || automationLimit >= 2147483647
        ? t("unlimited automation runs/month")
        : t("up to {{count}} automation runs/month", { count: automationLimit.toLocaleString() });
    if (plan.key === "ENTERPRISE_UNLIMITED") {
      return (
        <>
          <p>{`${planMonney} per team, billed monthly,`}</p>
          <p>{`${t("unlimited users")}`}</p>
          <p>{automationText}</p>
        </>
      );
    } else {
      return (
        <>
          <p>{`${planMonney} per team, billed monthly,`}</p>
          <p>{`max ${plan.maxUsers || 0} users per team`}</p>
          <p>{automationText}</p>
        </>
      );
    }
  };

  const renderPayment = () => {
    if (checkoutState.type !== "success") return;
    const { currency, lineItems, status } = checkoutState.checkout;
    return (
      <>
        <div className="w-full min-h-120 grid grid-cols-12 gap-4">
          <div className="col-span-12 sm:col-span-6 lg:col-span-6 gap-4 p-4">
            <div className="bg-blue-50 p-4 mb-4 rounded-xl flex justify-between items-center">
              <div className="flex items-center">
                <div className="p-3 mr-2 rounded-xl bg-blue-600">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-blue-700 flex items-center gap-2">
                    {selectedPlan.get("name")}
                  </h3>
                  <div className="text-sm text-gray-600">
                    {getLimitUserText(selectedPlan.toJS(), currency)}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 px-2 bg-yellow-400 rounded-lg">
                <Sparkles className="w-3 h-3 text-yellow-800" />
                <div className="text-sm">{t("AI included")}</div>
              </div>
            </div>
            <div className="p-4 mb-4 border border-gray-200 rounded-xl">
              <div className="mb-3 border-b border-b-gray-200">
                <h3 className="mb-3 font-semibold">{t("Billing Detail")}</h3>
              </div>
              {lineItems.map((item) => (
                <div key={item.id}>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="py-2 text-sm">{t("Quantity")}</div>
                    <div className="col-span-2 text-sm text-gray-500">
                      <div className="px-3 py-2">{item.quantity}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="py-2 text-sm">{t("Unit")}</div>
                    <div className="col-span-2 text-sm text-gray-500">
                      <div className="px-3 py-2">{item.unitAmount.amount}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="py-2 text-sm font-semibold">{t("Total")}</div>
                    <div className="col-span-2 text-sm font-semibold text-gray-500">
                      <div className="px-3 py-2">{item.total.amount}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 mb-4 border border-gray-200 rounded-xl">
              <div className="mb-3 border-b border-b-gray-200">
                <h3 className="mb-3 font-semibold">{t("Billing Address")}</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="py-2 text-sm">{t("Address Line 1")}</div>
                <div className="col-span-2 text-sm text-gray-500">
                  <div className="px-3 py-2">{billingAddress.get("addressLine1")}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="py-2 text-sm">{t("Address Line 2")}</div>
                <div className="col-span-2 text-sm text-gray-500">
                  <div className="px-3 py-2">{billingAddress.get("addressLine2")}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="py-2 text-sm">{t("City")}</div>
                <div className="col-span-2 text-sm text-gray-500">
                  <div className="px-3 py-2">{billingAddress.get("city")}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="py-2 text-sm">{t("Country")}</div>
                <div className="col-span-2 text-sm text-gray-500">
                  <div className="px-3 py-2">{billingAddress.get("country")}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="py-2 text-sm">{t("State")}</div>
                <div className="col-span-2 text-sm text-gray-500">
                  <div className="px-3 py-2">{billingAddress.get("state")}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="py-2 text-sm">{t("Postal Code")}</div>
                <div className="col-span-2 text-sm text-gray-500">
                  <div className="px-3 py-2">{billingAddress.get("postalCode")}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-span-12 sm:col-span-6 lg:col-span-6 gap-4 p-4">
            {status.type === "open" ? (
              <form>
                <PaymentElement options={{ layout: "accordion" }} />
              </form>
            ) : null}
            {status.type === "complete" ? (
              <div className="h-120 flex items-center justify-center gap-3">
                <div>
                  <CircleCheckBig className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <div className="text-lg font-semibold">{t("Payment Successful")}</div>
                  <div className="text-base text-gray-500">
                    {t("Thank you for choosing Sales CRM")}
                  </div>
                </div>
              </div>
            ) : null}
            {status.type === "expired" ? (
              <div className="h-120 flex items-center justify-center gap-3">
                <div>
                  <TriangleAlert className="w-8 h-8 text-red-600" />
                </div>
                <div>
                  <div className="text-lg font-semibold">{t("Payment Unsuccessful")}</div>
                  <div className="text-base text-gray-500">{t("Payment session has expired!")}</div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
        <div className="w-full p-4 flex items-center justify-between">
          <div className="flex items-center gap-2"></div>
          <div className="flex items-center gap-2">
            {status.type === "open" ? (
              <>
                <button
                  className="px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 cursor-pointer"
                  onClick={handleBack}
                >
                  {t("Back")}
                </button>
                <button
                  className="flex items-center gap-2 pl-3 px-3 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-50 disabled:text-gray-400 cursor-pointer"
                  onClick={handleClickPay}
                  disabled={loading}
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4" />
                  )}
                  {t("Pay")}
                </button>
              </>
            ) : (
              <>
                <button
                  className="px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 cursor-pointer"
                  onClick={handleClose}
                >
                  {t("Close")}
                </button>
              </>
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="min-w-6xl min-h-120">
      {checkoutState.type === "loading" ? (
        <div className="h-120 flex flex-col items-center justify-center">
          <div className="text-base">{t("Loading...")}</div>
          <div className={"px-4 py-4"}>
            <SmallLoading />
          </div>
        </div>
      ) : null}
      {checkoutState.type === "error" ? (
        <div className="h-120 flex flex-col items-center justify-center">
          <div className="text-base">{checkoutState.error.message}</div>
        </div>
      ) : null}
      {checkoutState.type === "success" ? renderPayment() : null}
    </div>
  );
};

export default CheckoutForm;
